import React, {useCallback, useMemo, useState} from 'react';
import {Modal, Spinner} from 'react-bootstrap';
import {ShowReconciledCart} from '../ui/ValidateOrderUI';
import {RemoteDataUI} from '../../common/ui/RemoteDataUI';
import {useOrderDeliveryAddress} from './useOrderDeliveryAddress';
import {getOrElse, isSuccess, mapRemoteData} from '../../common/domain/RemoteData.model';
import {ConfirmAddressUI} from '../ui/ConfirmAddressUI';
import {reconcileCart} from '../infrastructure/OrderApi';
import {useRemoteData} from '../../common/utils/useRemoteData';

const Steps = Object.freeze({
    RECONCILE_CART: 'VALIDATE_ORDER',
    CONFIRM_DELIVERY_ADDRESS: 'CONFIRM_DELIVERY_ADDRESS',
});

const BUTTON_LABELS = Object.freeze({
    CONTINUE: 'Continuar',
    CONTINUE_WITHOUT_INVALID: 'Continuar sin productos inválidos',
});

const TITLES = Object.freeze({
    [Steps.RECONCILE_CART]: 'Validación del pedido',
    [Steps.CONFIRM_DELIVERY_ADDRESS]: 'Confirmación de dirección de envío',
});

function getContinueButtonLabel(step, orderValidation) {
    if (
        step === Steps.RECONCILE_CART &&
        orderValidation &&
        orderValidation.valid === false
    ) {
        return BUTTON_LABELS.CONTINUE_WITHOUT_INVALID;
    }
    return BUTTON_LABELS.CONTINUE;
}

/**
 * PreCheckoutFlow component handles the pre-checkout process including order validation
 * and delivery address confirmation.
 * @param {object} props - Component props.
 * @param {boolean} props.show - Whether the modal is visible.
 * @param {function} props.onCancel - Callback when the modal is cancelled.
 * @param {function} props.onConfirm - Callback when the process is confirmed.
 * @returns {JSX.Element} The rendered component.
 */
export const PreCheckoutFlow = ({show, onCancel, onConfirm}) => {
    const [step, setStep] = useState(Steps.RECONCILE_CART);
    const [reconcileReloadKey, setReconcileReloadKey] = useState(0);

    // La identidad del callback cambia con reconcileReloadKey para que
    // useRemoteData relance la validación al resolver un producto sin stock.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const fetchReconciledCart = useCallback(() => reconcileCart(), [reconcileReloadKey]);

    const reconciledCartRemoteData = useRemoteData({
        initialData: null,
        dataFetchingCallback: fetchReconciledCart,
    });

    const handleItemResolved = useCallback(() => {
        setReconcileReloadKey((key) => key + 1);
    }, []);

    const {defaultDeliveryAddressRemoteData} = useOrderDeliveryAddress();

    const handleConfirm = useCallback(() => {
        if (step === Steps.RECONCILE_CART) {
            setStep(Steps.CONFIRM_DELIVERY_ADDRESS);
        } else if (step === Steps.CONFIRM_DELIVERY_ADDRESS) {
            onConfirm();
        }
    }, [step, onConfirm]);

    const currentTitle = useMemo(() => TITLES[step], [step]);

    const continueLabel = useMemo(
        () => getContinueButtonLabel(step, getOrElse(reconciledCartRemoteData, null)),
        [step, reconciledCartRemoteData]
    );

    const currentRemoteData =
        step === Steps.RECONCILE_CART
            ? reconciledCartRemoteData
            : defaultDeliveryAddressRemoteData;

    const shouldDisableContinue = useMemo(() => {
        const deprecatedProductsRemoteData = mapRemoteData(reconciledCartRemoteData, it => it?.deprecatedProducts)
        return !isSuccess(currentRemoteData) || getOrElse(deprecatedProductsRemoteData, []).length > 0;
    }, [currentRemoteData, reconciledCartRemoteData]);

    const renderValidationStep = useCallback(() => (
        <RemoteDataUI remoteData={reconciledCartRemoteData}>
            <RemoteDataUI.Success>
                {(data) => <ShowReconciledCart cart={data} onItemResolved={handleItemResolved}/>}
            </RemoteDataUI.Success>
            <RemoteDataUI.Pending>
                <div className="text-center my-3">
                    <Spinner/>
                </div>
            </RemoteDataUI.Pending>
            <RemoteDataUI.Failure>
                <div className="alert alert-danger py-2">
                    Fallo en la validación. Contacte con nosotros.
                </div>
            </RemoteDataUI.Failure>
        </RemoteDataUI>
    ), [reconciledCartRemoteData, handleItemResolved]);

    const renderAddressStep = useCallback(() => (
        <RemoteDataUI remoteData={defaultDeliveryAddressRemoteData}>
            <RemoteDataUI.Success>
                {(data) => <ConfirmAddressUI address={data}/>}
            </RemoteDataUI.Success>
            <RemoteDataUI.Pending>
                <div className="text-center my-3">
                    <Spinner/>
                </div>
            </RemoteDataUI.Pending>
            <RemoteDataUI.Failure>
                <div className="alert alert-danger py-2">
                    Error al cargar dirección de envío. Contacte con nosotros.
                </div>
            </RemoteDataUI.Failure>
        </RemoteDataUI>
    ), [defaultDeliveryAddressRemoteData]);

    const renderStepContent = useCallback(() => {
        if (step === Steps.RECONCILE_CART) return renderValidationStep();
        if (step === Steps.CONFIRM_DELIVERY_ADDRESS) return renderAddressStep();
        return null;
    }, [step, renderValidationStep, renderAddressStep]);

    return (
        <Modal show={show} onHide={onCancel} centered>
            <Modal.Header closeButton>
                <Modal.Title>{currentTitle}</Modal.Title>
            </Modal.Header>
            <Modal.Body>{renderStepContent()}</Modal.Body>
            <Modal.Footer>
                <div className="d-flex gap-2">
                    <button
                        className="btn btn-secondary"
                        type="button"
                        onClick={onCancel}
                    >
                        Cancelar
                    </button>
                    <button
                        className="btn btn-primary"
                        type="button"
                        onClick={handleConfirm}
                        disabled={shouldDisableContinue}
                    >
                        {continueLabel}
                    </button>
                </div>
            </Modal.Footer>
        </Modal>
    );
};
